import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Chart, registerables } from 'chart.js';
import { CarteraService, CarteraKPIs, ClienteItem, ClienteDetail, ClienteFilters, DistribucionMora } from 'src/app/services/cartera/cartera.service';
import { CarteraCacheService } from 'src/app/services/cartera/cartera-cache.service';
import { ProcessExecutionsService, ProcessExecution, JobDefinition } from 'src/app/services/process-executions/process-executions.service';

Chart.register(...registerables);

type Tab = 'resumen' | 'clientes' | 'analisis' | 'operacion' | 'procesos' | 'morosidad' | 'comportamiento' | 'gestion-cobro' | 'anomalias';
type AnomaliaTipo = 'sin-recalc' | 'cuotas-sin-fee' | 'cuotas-fee-jamas' | 'cuotas-fee-regenerar' | 'cuotas-fee-desactualizado' | 'mora-incoherente' | 'bloqueados-sin-mora' | 'criticos-180';
type AnalisisSubTab = 'top' | 'vintage' | 'roll' | 'cure' | 'ecl' | 'aliado' | 'prediccion' | 'reactivaciones';
type OperacionSubTab = 'movimientos' | 'recaudacion' | 'proximos_c004' | 'aging';
type DatePreset = 'hoy' | 'ayer' | 'esta_semana' | 'este_mes' | 'mes_pasado' | 'lifetime' | 'custom';

@Component({
  selector: 'app-cartera',
  templateUrl: './cartera.component.html',
  styleUrls: ['./cartera.component.css']
})
export class CarteraComponent implements OnInit, OnDestroy, AfterViewInit {
  activeTab: Tab = 'resumen';
  analisisTab: AnalisisSubTab = 'top';

  // Análisis state
  analisisData: { [k in AnalisisSubTab]?: any[] } = {};
  analisisLoading: { [k in AnalisisSubTab]?: boolean } = {};
  analisisError: { [k in AnalisisSubTab]?: string } = {};

  // Operación state
  operacionTab: OperacionSubTab = 'movimientos';
  movimientos: any = null;
  recaudacion: any = null;
  proximosC004: any = null;
  aging: any = null;
  proximosRangoMin = 50;
  proximosRangoMax = 60;
  loadingMov = false;
  loadingRecaudacion = false;
  loadingProx = false;
  loadingAging = false;

  // Anomalías state
  morosidadData: any = null;
  loadingMorosidad = false;
  // Distribución del dinero en la calle (tab Morosidad, tabla Metabase)
  distribucionDinero: any = null;
  distribucionDineroLoading = false;
  distribucionDineroError = '';
  comportamientoData: any = null;
  loadingComportamiento = false;
  gestionCobroData: any = null;
  loadingGestionCobro = false;

  // ─── Configuración de indicadores (drawer) ───
  showConfigDrawer = false;
  configEditing: any = null;
  configOriginal: any = null;
  configDefaults: any = null;
  configValidationError: string | null = null;
  configSaving = false;
  anomaliasResumen: any = null;
  anomaliaTipo: AnomaliaTipo | null = null;
  anomaliaTitle: string = '';
  anomaliaItems: any[] = [];
  anomaliaTotal: number = 0;
  anomaliaPage: number = 1;
  anomaliaPageSize: number = 50;
  loadingAnomaliasResumen = false;
  loadingAnomaliaList = false;
  showAnomHelp = false;

  // Date filters (compartido entre movimientos y recaudación)
  movPreset: DatePreset = 'hoy';
  movFrom = '';
  movTo = '';
  recPreset: DatePreset = 'este_mes';
  recFrom = '';
  recTo = '';

  // KPIs
  kpis: CarteraKPIs | null = null;
  loadingKpis = false;
  distribucion: DistribucionMora | null = null;
  escalafon: any = null;
  initialLoaded = false;

  // Clientes
  clientes: ClienteItem[] = [];
  total = 0;
  totalPages = 0;
  page = 1;
  pageSize = 50;
  loadingClientes = false;
  clientesEverLoaded = false;

  filters: ClienteFilters = {
    dias_min: null, dias_max: null, estado: '', bloqueo_tipo: '',
    tiene_reactivaciones: '', monto_min: null, search: '',
    sort_by: 'mora.actual', sort_dir: -1
  };

  searchDebounce: any = null;

  // Bulk selection
  selectedIds = new Set<string>();
  bulkActionLoading: string | null = null;
  bulkMessage: string | null = null;

  // Quick filter presets
  activeQuickFilter: string | null = null;

  // Drawer
  drawerOpen = false;
  drawerLoading = false;
  selectedCliente: ClienteDetail | null = null;

  // Jobs / actividad
  jobs: JobDefinition[] = [];
  recentRuns: ProcessExecution[] = [];
  triggering: string | null = null;

  kpiRefreshTimer: any = null;
  activityRefreshTimer: any = null;
  error: string | null = null;

  // Charts
  @ViewChild('bucketChart') bucketChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('feesChart')   feesChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('distChart')   distChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('estadoChart') estadoChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('agingChart')  agingChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('nplGaugeChart') nplGaugeChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('vintageChart') vintageChartRef!: ElementRef<HTMLCanvasElement>;

  private bucketChart?: Chart;
  private feesChart?: Chart;
  private distChart?: Chart;
  private estadoChart?: Chart;
  private agingChart?: Chart;
  private nplGaugeChart?: Chart;
  private vintageChart?: Chart;

  // Timestamps de última actualización (mostrados como "Actualizado HH:MM")
  morosidadUpdatedAt = 0;
  comportamientoUpdatedAt = 0;
  gestionCobroUpdatedAt = 0;
  kpisUpdatedAt = 0;
  // Loading "discreto" (refresh en background, no bloquea UI con placeholder)
  refreshingMorosidad = false;
  refreshingComportamiento = false;
  refreshingGestionCobro = false;
  refreshingKpis = false;

  // Modal lateral con detalle de métrica de morosidad
  selectedMetrica: any = null;
  openMetricaDetail(m: any) { this.selectedMetrica = m; }
  closeMetricaDetail() { this.selectedMetrica = null; }

  constructor(
    public carteraSvc: CarteraService,
    public processSvc: ProcessExecutionsService,
    public cache: CarteraCacheService,
    private router: Router,
    private cd: ChangeDetectorRef
  ) {}

  fmtCacheTimestamp(epochMs: number): string { return this.cache.formatTimestamp(epochMs); }

  async ngOnInit() {
    await this.loadKPIs();
    this.loadDistribucion();
    this.loadEscalafon();
    this.loadJobs();
    this.loadActivity();
    this.kpiRefreshTimer = setInterval(() => this.loadKPIs(), 60000);
    this.activityRefreshTimer = setInterval(() => this.loadActivity(), 10000);
  }

  ngAfterViewInit() {
    setTimeout(() => this.renderCharts(), 200);
  }

  ngOnDestroy() {
    if (this.kpiRefreshTimer) clearInterval(this.kpiRefreshTimer);
    if (this.activityRefreshTimer) clearInterval(this.activityRefreshTimer);
    this.bucketChart?.destroy();
    this.feesChart?.destroy();
    this.distChart?.destroy();
    this.estadoChart?.destroy();
    this.agingChart?.destroy();
    this.nplGaugeChart?.destroy();
    this.vintageChart?.destroy();
  }

  setTab(tab: Tab) {
    this.activeTab = tab;
    if (tab === 'clientes' && !this.clientesEverLoaded) this.loadClientes();
    if (tab === 'resumen') setTimeout(() => this.renderCharts(), 100);
    if (tab === 'analisis') this.loadAnalisis(this.analisisTab);
    if (tab === 'operacion') this.loadOperacion(this.operacionTab);
    if (tab === 'morosidad') {
      if (!this.morosidadData) this.loadMorosidad();
      else setTimeout(() => this.renderMorosidadCharts(), 100);
      this.loadDistribucionDinero();
    }
    if (tab === 'comportamiento' && !this.comportamientoData) this.loadComportamiento();
    if (tab === 'gestion-cobro' && !this.gestionCobroData) this.loadGestionCobro();
    if (tab === 'anomalias' && !this.anomaliasResumen) this.loadAnomaliasResumen();
  }

  async loadAnomaliasResumen() {
    this.loadingAnomaliasResumen = true;
    try {
      const r = await this.carteraSvc.getAnomaliasResumen();
      if (r?.data) this.anomaliasResumen = r.data;
    } catch (e) { console.error(e); }
    finally { this.loadingAnomaliasResumen = false; }
  }

  async loadMorosidad() {
    // 1) Intentar cache primero (UX inmediata)
    const cached = this.cache.get<any>('morosidad');
    if (cached) {
      this.morosidadData = cached.data;
      this.morosidadUpdatedAt = cached.updated_at;
      this.loadingMorosidad = false;
      this.refreshingMorosidad = true;       // spinner discreto
      setTimeout(() => this.renderMorosidadCharts(), 100);
    } else {
      this.loadingMorosidad = true;            // placeholder grande (primera vez)
    }
    // 2) Refrescar en background siempre
    try {
      const r = await this.carteraSvc.getIndicadoresMorosidad();
      if (r?.data) {
        this.morosidadData = r.data;
        this.morosidadUpdatedAt = Date.now();
        this.cache.set('morosidad', r.data);
        setTimeout(() => this.renderMorosidadCharts(), 100);
      }
    } catch (e) { console.error(e); }
    finally {
      this.loadingMorosidad = false;
      this.refreshingMorosidad = false;
    }
  }

  async loadDistribucionDinero(): Promise<void> {
    if (this.distribucionDinero) return; // ya cargado
    this.distribucionDineroLoading = true;
    this.distribucionDineroError = '';
    try {
      const r: any = await this.carteraSvc.getDistribucionDinero();
      this.distribucionDinero = r?.data || null;
    } catch (e: any) {
      this.distribucionDineroError = e?.error?.msg || e?.message || 'Error cargando distribución';
    } finally {
      this.distribucionDineroLoading = false;
    }
  }

  private renderMorosidadCharts() {
    if (!this.morosidadData) return;
    this.renderAgingChart();
    this.renderNplGaugeChart();
    this.renderVintageChart();
  }

  private renderAgingChart() {
    if (!this.agingChartRef || !this.morosidadData?.aging) return;
    const aging = this.morosidadData.aging;
    if (this.agingChart) this.agingChart.destroy();
    this.agingChart = new Chart(this.agingChartRef.nativeElement, {
      type: 'bar',
      data: {
        labels: aging.map((b: any) => `${b.label}\n(${b.range})`),
        datasets: [{
          label: 'Monto ($)',
          data: aging.map((b: any) => b.monto),
          backgroundColor: aging.map((b: any) => b.color || '#94a3b8'),
          borderRadius: 6,
          borderSkipped: false
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const b = aging[ctx.dataIndex];
                return [`Monto: $${(b.monto || 0).toLocaleString()}`, `Clientes: ${(b.count || 0).toLocaleString()}`, `% GLP: ${b.pct_glp}%`];
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: (v: any) => '$' + (v / 1000).toFixed(0) + 'k'
            }
          },
          x: { ticks: { font: { size: 11 } } }
        }
      }
    });
  }

  private renderNplGaugeChart() {
    if (!this.nplGaugeChartRef || !this.morosidadData?.npl) return;
    const npl = this.morosidadData.npl.ratio_glp_pct || 0;
    const rest = 100 - npl;
    const color = npl > 25 ? '#dc2626' : npl > 15 ? '#f59e0b' : npl > 5 ? '#facc15' : '#22c55e';
    if (this.nplGaugeChart) this.nplGaugeChart.destroy();
    this.nplGaugeChart = new Chart(this.nplGaugeChartRef.nativeElement, {
      type: 'doughnut',
      data: {
        labels: ['NPL', 'Resto'],
        datasets: [{
          data: [npl, rest],
          backgroundColor: [color, '#e2e8f0'],
          borderWidth: 0,
          circumference: 180,
          rotation: 270
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '75%',
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: { label: (ctx: any) => `${ctx.label}: ${ctx.parsed.toFixed(2)}%` }
          }
        }
      }
    });
  }

  private renderVintageChart() {
    if (!this.vintageChartRef || !this.morosidadData?.vintage_cohorts?.length) return;
    const cohorts = this.morosidadData.vintage_cohorts;
    if (this.vintageChart) this.vintageChart.destroy();
    this.vintageChart = new Chart(this.vintageChartRef.nativeElement, {
      type: 'line',
      data: {
        labels: cohorts.map((c: any) => c.cohort),
        datasets: [
          { label: 'Mora actual %', data: cohorts.map((c: any) => c.mora_actual_pct), borderColor: '#f59e0b', backgroundColor: 'rgba(245,158,11,0.1)', fill: false, tension: 0.3 },
          { label: 'Mora >30d %', data: cohorts.map((c: any) => c.mora_30_pct), borderColor: '#f97316', fill: false, tension: 0.3 },
          { label: 'Mora >90d %', data: cohorts.map((c: any) => c.mora_90_pct), borderColor: '#dc2626', fill: false, tension: 0.3 },
          { label: 'Mora >180d %', data: cohorts.map((c: any) => c.mora_180_pct), borderColor: '#7f1d1d', fill: false, tension: 0.3 }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 }}}},
        scales: {
          y: { beginAtZero: true, ticks: { callback: (v: any) => v + '%' }},
          x: { ticks: { font: { size: 10 }}}
        }
      }
    });
  }

  async loadComportamiento() {
    const cached = this.cache.get<any>('comportamiento');
    if (cached) {
      this.comportamientoData = cached.data;
      this.comportamientoUpdatedAt = cached.updated_at;
      this.loadingComportamiento = false;
      this.refreshingComportamiento = true;
    } else {
      this.loadingComportamiento = true;
    }
    try {
      const r = await this.carteraSvc.getComportamientoPago();
      if (r?.data) {
        this.comportamientoData = r.data;
        this.comportamientoUpdatedAt = Date.now();
        this.cache.set('comportamiento', r.data);
      }
    } catch (e) { console.error(e); }
    finally {
      this.loadingComportamiento = false;
      this.refreshingComportamiento = false;
    }
  }

  async triggerSnapshotNow() {
    this.loadingComportamiento = true;
    try {
      await this.carteraSvc.triggerSnapshotNow();
      await this.loadComportamiento();
    } catch (e) { console.error(e); }
    finally { this.loadingComportamiento = false; }
  }

  async loadGestionCobro() {
    const cached = this.cache.get<any>('gestion-cobro');
    if (cached) {
      this.gestionCobroData = cached.data;
      this.gestionCobroUpdatedAt = cached.updated_at;
      this.loadingGestionCobro = false;
      this.refreshingGestionCobro = true;
    } else {
      this.loadingGestionCobro = true;
    }
    try {
      const r = await this.carteraSvc.getGestionCobro();
      if (r?.data) {
        this.gestionCobroData = r.data;
        this.gestionCobroUpdatedAt = Date.now();
        this.cache.set('gestion-cobro', r.data);
      }
    } catch (e) { console.error(e); }
    finally {
      this.loadingGestionCobro = false;
      this.refreshingGestionCobro = false;
    }
  }

  // ─── Drawer config ───
  async openConfigDrawer() {
    try {
      const r = await this.carteraSvc.getCarteraConfig();
      this.configEditing = JSON.parse(JSON.stringify(r?.data || {}));
      this.configOriginal = JSON.parse(JSON.stringify(r?.data || {}));
      this.configDefaults = r?.defaults || null;
      this.showConfigDrawer = true;
      this.configValidationError = null;
    } catch (e) { console.error(e); }
  }

  closeConfigDrawer() {
    this.showConfigDrawer = false;
    this.configEditing = null;
    this.configValidationError = null;
  }

  addConfigBucket() {
    if (!this.configEditing) return;
    const buckets = this.configEditing.aging_buckets;
    const last = buckets[buckets.length - 1];
    if (last.max === null) last.max = (last.min || 0) + 30;
    buckets.push({ id: 'nuevo_' + Date.now(), label: 'Nuevo', min: last.max + 1, max: null, color: '#94a3b8' });
  }

  removeConfigBucket(idx: number) {
    if (!this.configEditing) return;
    if (this.configEditing.aging_buckets.length <= 2) return;
    this.configEditing.aging_buckets.splice(idx, 1);
    const last = this.configEditing.aging_buckets[this.configEditing.aging_buckets.length - 1];
    last.max = null;
  }

  async previewConfig() {
    if (!this.configEditing) return;
    try {
      await this.carteraSvc.previewCarteraConfig(this.configEditing);
      this.configValidationError = null;
    } catch (e: any) {
      this.configValidationError = e?.error?.error || 'Configuración inválida';
    }
  }

  async saveConfigGlobal() {
    if (!this.configEditing) return;
    this.configSaving = true;
    try {
      await this.carteraSvc.setCarteraConfig(this.configEditing);
      this.morosidadData = null;
      await this.loadMorosidad();
      this.closeConfigDrawer();
    } catch (e: any) {
      this.configValidationError = e?.error?.error || 'Error al guardar';
    } finally { this.configSaving = false; }
  }

  restoreDefaults() {
    if (!this.configDefaults) return;
    this.configEditing = JSON.parse(JSON.stringify(this.configDefaults));
    this.configValidationError = null;
  }

  onParThresholdsChange(value: string) {
    if (!this.configEditing) return;
    const arr = (value || '')
      .split(',')
      .map((v: string) => parseInt(v.trim(), 10))
      .filter((v: number) => !isNaN(v) && v > 0);
    this.configEditing.par_thresholds = arr;
    this.previewConfig();
  }

  async openAnomaliaList(tipo: AnomaliaTipo) {
    this.anomaliaTipo = tipo;
    this.anomaliaPage = 1;
    this.anomaliaItems = [];
    await this.loadAnomaliaList();
  }

  async loadAnomaliaList() {
    if (!this.anomaliaTipo) return;
    this.loadingAnomaliaList = true;
    try {
      const r = await this.carteraSvc.getAnomaliaList(this.anomaliaTipo, this.anomaliaPage, this.anomaliaPageSize);
      if (r?.items) {
        this.anomaliaItems = r.items;
        this.anomaliaTotal = r.total || r.items.length;
        this.anomaliaTitle = r.title || '';
      }
    } catch (e) { console.error(e); }
    finally { this.loadingAnomaliaList = false; }
  }

  closeAnomaliaList() {
    this.anomaliaTipo = null;
    this.anomaliaItems = [];
  }

  downloadAnomaliaXlsx() {
    if (!this.anomaliaTipo) return;
    const url = this.carteraSvc.downloadAnomaliaXlsx(this.anomaliaTipo);
    const token = localStorage.getItem('x-token') || '';
    fetch(url, { headers: { authorization: token } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `anomalia_${this.anomaliaTipo}_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
      });
  }

  anomaliaColumns(): string[] {
    if (!this.anomaliaItems.length) return [];
    return Object.keys(this.anomaliaItems[0]).filter(k => k !== 'id' && k !== 'person_id' && k !== 'cuota_id');
  }
  formatAnomaliaCell(v: any): string {
    if (v == null) return '—';
    if (typeof v === 'boolean') return v ? 'Sí' : 'No';
    if (typeof v === 'object') return JSON.stringify(v);
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return this.fmtDate(v);
    return String(v);
  }

  setOperacionTab(sub: OperacionSubTab) {
    this.operacionTab = sub;
    this.loadOperacion(sub);
  }

  async loadOperacion(sub: OperacionSubTab) {
    if (sub === 'movimientos' && this.movimientos) return;
    if (sub === 'recaudacion' && this.recaudacion) return;
    if (sub === 'proximos_c004' && this.proximosC004) return;
    if (sub === 'aging' && this.aging) return;
    try {
      if (sub === 'movimientos') {
        this.loadingMov = true;
        const r = await this.carteraSvc.getMovimientosHoy(this.buildDateOpts('mov'));
        if (r?.data) this.movimientos = r.data;
      } else if (sub === 'recaudacion') {
        this.loadingRecaudacion = true;
        const r = await this.carteraSvc.getRecaudacion(this.buildDateOpts('rec'));
        if (r?.data) this.recaudacion = r.data;
      } else if (sub === 'proximos_c004') {
        this.loadingProx = true;
        const r = await this.carteraSvc.getProximosC004(this.proximosRangoMin, this.proximosRangoMax, 200);
        if (r?.data) this.proximosC004 = r.data;
      } else if (sub === 'aging') {
        this.loadingAging = true;
        const r = await this.carteraSvc.getAging();
        if (r?.data) this.aging = r.data;
      }
    } catch (e) { console.error(e); }
    finally {
      this.loadingMov = false;
      this.loadingRecaudacion = false;
      this.loadingProx = false;
      this.loadingAging = false;
    }
  }

  private buildDateOpts(prefix: 'mov' | 'rec'): { preset?: string; from?: string; to?: string } {
    const preset = prefix === 'mov' ? this.movPreset : this.recPreset;
    const from = prefix === 'mov' ? this.movFrom : this.recFrom;
    const to = prefix === 'mov' ? this.movTo : this.recTo;
    if (preset === 'custom') return { from, to };
    return { preset };
  }

  setMovPreset(p: DatePreset) {
    this.movPreset = p;
    this.movimientos = null;
    this.loadOperacion('movimientos');
  }
  applyMovCustom() {
    this.movPreset = 'custom';
    this.movimientos = null;
    this.loadOperacion('movimientos');
  }
  setRecPreset(p: DatePreset) {
    this.recPreset = p;
    this.recaudacion = null;
    this.loadOperacion('recaudacion');
  }
  applyRecCustom() {
    this.recPreset = 'custom';
    this.recaudacion = null;
    this.loadOperacion('recaudacion');
  }

  reloadProximosC004() {
    this.proximosC004 = null;
    this.loadOperacion('proximos_c004');
  }

  setAnalisisTab(sub: AnalisisSubTab) {
    this.analisisTab = sub;
    this.loadAnalisis(sub);
  }

  async loadAnalisis(sub: AnalisisSubTab) {
    if (this.analisisData[sub] && this.analisisData[sub]!.length > 0) return; // cache
    this.analisisLoading[sub] = true;
    this.analisisError[sub] = undefined;
    try {
      let r: any;
      switch (sub) {
        case 'top':           r = await this.carteraSvc.getAnalisisTopCriticos(); break;
        case 'vintage':       r = await this.carteraSvc.getAnalisisVintage(); break;
        case 'roll':          r = await this.carteraSvc.getAnalisisRollRate(); break;
        case 'cure':          r = await this.carteraSvc.getAnalisisCureRate(); break;
        case 'ecl':           r = await this.carteraSvc.getAnalisisECL(); break;
        case 'aliado':        r = await this.carteraSvc.getAnalisisPorAliado(); break;
        case 'prediccion':    r = await this.carteraSvc.getAnalisisPrediccion(); break;
        case 'reactivaciones': r = await this.carteraSvc.getAnalisisReactivaciones(); break;
      }
      this.analisisData[sub] = r?.data || [];
    } catch (e: any) {
      this.analisisError[sub] = e?.message || 'Error al cargar';
    } finally {
      this.analisisLoading[sub] = false;
    }
  }

  async refreshAnalisisCache() {
    try {
      await this.carteraSvc.refreshAnalisisCache();
      this.analisisData = {};
      this.loadAnalisis(this.analisisTab);
    } catch (e) { console.error(e); }
  }

  getColumns(rows: any[] | undefined): string[] {
    if (!rows || rows.length === 0) return [];
    return Object.keys(rows[0]);
  }

  formatCell(v: any): string {
    if (v == null) return '—';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  }

  async loadKPIs() {
    this.loadingKpis = true;
    try {
      const r = await this.carteraSvc.getKPIs();
      if (r?.data) {
        this.kpis = r.data;
        setTimeout(() => this.renderCharts(), 50);
      }
    } catch (e) { console.error('loadKPIs:', e); }
    finally {
      this.loadingKpis = false;
      this.initialLoaded = true;
    }
  }

  async loadDistribucion() {
    try {
      const r = await this.carteraSvc.getDistribucion();
      if (r?.data) {
        this.distribucion = r.data;
        setTimeout(() => this.renderDistChart(), 50);
      }
    } catch (e) { console.error(e); }
  }

  async loadEscalafon() {
    try {
      const r = await this.carteraSvc.getEscalafonFees();
      if (r?.data) this.escalafon = r.data;
    } catch (e) { console.error(e); }
  }

  async loadClientes() {
    this.loadingClientes = true;
    this.error = null;
    try {
      const r = await this.carteraSvc.listClientes({ ...this.filters, page: this.page, page_size: this.pageSize });
      if (r?.data) {
        this.clientes = r.data.items || [];
        this.total = r.data.total || 0;
        this.totalPages = r.data.total_pages || 0;
      }
    } catch (e: any) {
      this.error = e?.message || 'Error al cargar clientes';
    } finally {
      this.loadingClientes = false;
      this.clientesEverLoaded = true;
    }
  }

  async loadJobs() {
    try {
      const r = await this.processSvc.listJobs();
      if (r?.jobs) this.jobs = r.jobs;
    } catch (e) { console.error(e); }
  }

  async loadActivity() {
    try {
      const r = await this.carteraSvc.getActividad(15);
      if (r?.data?.runs) this.recentRuns = r.data.runs;
    } catch (e) { console.error(e); }
  }

  // ── Charts ───────────────────────────────────────────
  private renderCharts() {
    if (!this.kpis) return;
    this.renderBucketChart();
    this.renderFeesChart();
    this.renderEstadoChart();
    this.renderDistChart();
  }

  private renderBucketChart() {
    if (!this.kpis || !this.bucketChartRef) return;
    const buckets = this.kpis.buckets;
    const labels = Object.keys(buckets);
    const data = labels.map(k => buckets[k].count);
    // Escala monocromática azul (de claro = al día → oscuro = crítico)
    const colors = ['#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#1d4ed8', '#1e3a8a'];

    if (this.bucketChart) this.bucketChart.destroy();
    this.bucketChart = new Chart(this.bucketChartRef.nativeElement, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{ data, backgroundColor: colors, borderWidth: 0 }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '60%',
        plugins: {
          legend: { position: 'right', labels: { font: { size: 11 }, boxWidth: 12, padding: 8 } },
          title: { display: false }
        }
      }
    });
  }

  private renderFeesChart() {
    if (!this.kpis || !this.feesChartRef) return;
    const f = this.kpis.fees;

    if (this.feesChart) this.feesChart.destroy();
    this.feesChart = new Chart(this.feesChartRef.nativeElement, {
      type: 'bar',
      data: {
        labels: ['C001 pendientes', 'C004 pendientes', 'Pagados lifetime'],
        datasets: [
          {
            label: 'Cantidad',
            data: [f.c001_pendientes_count, f.c004_pendientes_count, f.pagados_lifetime_count],
            backgroundColor: ['#3b82f6', '#8b5cf6', '#22c55e'],
            yAxisID: 'y',
            borderRadius: 4
          },
          {
            label: 'Monto $',
            data: [f.c001_pendientes_monto, f.c004_pendientes_monto, f.pagados_lifetime_monto],
            backgroundColor: ['#93c5fd', '#c4b5fd', '#86efac'],
            yAxisID: 'y1',
            borderRadius: 4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top', labels: { font: { size: 11 } } }
        },
        scales: {
          y:  { position: 'left',  title: { display: true, text: 'Cantidad' }, ticks: { font: { size: 10 } } },
          y1: { position: 'right', title: { display: true, text: '$ Monto' }, ticks: { font: { size: 10 } }, grid: { drawOnChartArea: false } }
        }
      }
    });
  }

  private renderEstadoChart() {
    if (!this.kpis || !this.estadoChartRef) return;
    const e = this.kpis.estado;
    const activos = (this.kpis.total_clientes || 0) - (e.bloqueados_total || 0);

    if (this.estadoChart) this.estadoChart.destroy();
    this.estadoChart = new Chart(this.estadoChartRef.nativeElement, {
      type: 'pie',
      data: {
        labels: ['Activos', 'Bloqueo manual', 'Bloqueo automático', 'Reactivados'],
        datasets: [{
          data: [
            activos,
            e.bloqueo_manual,
            e.bloqueados_total - e.bloqueo_manual,
            e.reactivados_alguna_vez
          ],
          // Paleta cohesiva con azul de marca: blue / sky / slate / violet
          backgroundColor: ['#3b82f6', '#0ea5e9', '#64748b', '#8b5cf6'],
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'right', labels: { font: { size: 11 }, boxWidth: 12, padding: 8 } }
        }
      }
    });
  }

  private renderDistChart() {
    if (!this.distribucion || !this.distChartRef) return;
    const buckets = this.distribucion.buckets || [];

    if (this.distChart) this.distChart.destroy();
    this.distChart = new Chart(this.distChartRef.nativeElement, {
      type: 'bar',
      data: {
        labels: buckets.map(b => b.range),
        datasets: [{
          label: 'Clientes',
          data: buckets.map(b => b.count),
          // Escala azul gradual de claro (mora baja) a oscuro (crítico)
          backgroundColor: buckets.map(b => {
            const m = b.min || 0;
            if (m <= 15) return '#bfdbfe';
            if (m <= 60) return '#93c5fd';
            if (m <= 90) return '#60a5fa';
            if (m <= 180) return '#3b82f6';
            return '#1e3a8a';
          }),
          borderRadius: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { font: { size: 10 } } },
          y: { ticks: { font: { size: 10 } } }
        }
      }
    });
  }

  // ── Filtros ──────────────────────────────────────────
  onSearchChange() {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => {
      this.page = 1;
      this.loadClientes();
    }, 400);
  }
  applyFilters() { this.page = 1; this.loadClientes(); }

  // Quick filters predefinidos
  applyQuickFilter(name: string) {
    this.resetFilters();
    this.activeQuickFilter = name;
    switch (name) {
      case 'criticos':       this.filters.dias_min = 91; break;
      case 'cerca_c004':     this.filters.dias_min = 50; this.filters.dias_max = 65; break;
      case 'recien':         this.filters.dias_min = 1; this.filters.dias_max = 3; break;
      case 'reactivados':    this.filters.tiene_reactivaciones = 'true'; break;
      case 'bloqueados_auto': this.filters.estado = 'bloqueado'; this.filters.bloqueo_tipo = 'automatico'; break;
      case 'fees_c001':      this.filters.dias_min = 4; this.filters.dias_max = 65; break;
      case 'fees_c004':      this.filters.dias_min = 61; this.filters.dias_max = 90; break;
    }
    this.page = 1;
    this.loadClientes();
  }

  // Bulk select
  toggleSelectAll(checked: boolean) {
    if (checked) {
      this.clientes.forEach(c => this.selectedIds.add(c.id));
    } else {
      this.clientes.forEach(c => this.selectedIds.delete(c.id));
    }
  }
  toggleSelect(id: string) {
    if (this.selectedIds.has(id)) this.selectedIds.delete(id);
    else this.selectedIds.add(id);
  }
  isSelected(id: string): boolean { return this.selectedIds.has(id); }
  allPageSelected(): boolean {
    return this.clientes.length > 0 && this.clientes.every(c => this.selectedIds.has(c.id));
  }
  clearSelection() { this.selectedIds.clear(); this.bulkMessage = null; }

  async bulkRecalc() {
    if (this.selectedIds.size === 0) return;
    if (!confirm(`¿Recalcular mora de ${this.selectedIds.size} clientes?`)) return;
    this.bulkActionLoading = 'recalc';
    this.bulkMessage = null;
    try {
      const r = await this.carteraSvc.recalcBatch([...this.selectedIds]);
      this.bulkMessage = `✓ ${r?.data?.ok || 0} recalculados · ${r?.data?.failed || 0} fallidos`;
      this.loadClientes();
    } catch (e: any) {
      this.bulkMessage = '✗ Error: ' + (e?.error?.error || e?.message);
    } finally {
      this.bulkActionLoading = null;
    }
  }

  exportSeleccion() {
    if (this.selectedIds.size === 0) return;
    this.downloadCsv([...this.selectedIds], 'cartera_seleccion');
  }
  exportFiltrado() {
    this.downloadCsv(this.clientes.map(c => c.id), `cartera_pagina_${this.page}`);
  }
  private downloadCsv(ids: string[], baseName: string) {
    const rows = this.clientes.filter(c => ids.includes(c.id));
    if (rows.length === 0) { alert('Sin filas para exportar'); return; }
    const cols = ['code','identity','full_name','quota_active','mora_dias','mora_count','monto_adeudado',
                  'fees_c001_pend_count','fees_c001_pend_monto','fees_c004_pend_count','fees_c004_pend_monto',
                  'penalty_reactivation_count','user_blocked_manually','reactivation_blocked','tendencia','last_recalc'];
    const header = cols.join(',');
    const csv = [header, ...rows.map(r => cols.map(c => {
      const v = (r as any)[c];
      if (v == null) return '';
      const s = typeof v === 'string' ? v.replace(/"/g, '""') : String(v);
      return /[,"\n]/.test(s) ? `"${s}"` : s;
    }).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${baseName}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  }
  resetFilters() {
    this.filters = {
      dias_min: null, dias_max: null, estado: '', bloqueo_tipo: '',
      tiene_reactivaciones: '', monto_min: null, search: '',
      sort_by: 'mora.actual', sort_dir: -1
    };
    this.activeQuickFilter = null;
    this.page = 1;
    this.loadClientes();
  }
  setSort(by: string) {
    if (this.filters.sort_by === by) this.filters.sort_dir = this.filters.sort_dir === 1 ? -1 : 1;
    else { this.filters.sort_by = by; this.filters.sort_dir = -1; }
    this.loadClientes();
  }
  prevPage() { if (this.page > 1) { this.page--; this.loadClientes(); } }
  nextPage() { if (this.page < this.totalPages) { this.page++; this.loadClientes(); } }

  // Acciones drawer
  actionLoading: string | null = null;
  actionMessage: string | null = null;

  async recalcCliente() {
    if (!this.selectedCliente) return;
    this.actionLoading = 'recalc';
    this.actionMessage = null;
    try {
      const r = await this.carteraSvc.recalcCliente(this.selectedCliente.id);
      this.actionMessage = r?.data?.recalculated ? '✓ Mora recalculada' : '✓ Datos ya estaban frescos';
      // Re-cargar el detalle
      const fresh = await this.carteraSvc.getCliente(this.selectedCliente.id);
      if (fresh?.data) this.selectedCliente = fresh.data;
    } catch (e: any) {
      this.actionMessage = '✗ Error: ' + (e?.error?.error || e?.message);
    } finally { this.actionLoading = null; }
  }

  async toggleBloqueo() {
    if (!this.selectedCliente) return;
    const willBlock = this.selectedCliente.quota_active;
    const reason = willBlock ? prompt('Motivo del bloqueo:') : null;
    if (willBlock && !reason) return;
    if (!confirm(`¿${willBlock ? 'Bloquear' : 'Desbloquear'} a ${this.selectedCliente.full_name || this.selectedCliente.code}?`)) return;
    this.actionLoading = 'bloqueo';
    this.actionMessage = null;
    try {
      await this.carteraSvc.toggleBloqueoManual(this.selectedCliente.id, willBlock, reason || undefined);
      this.actionMessage = willBlock ? '✓ Cliente bloqueado' : '✓ Cliente desbloqueado';
      const fresh = await this.carteraSvc.getCliente(this.selectedCliente.id);
      if (fresh?.data) this.selectedCliente = fresh.data;
      this.loadClientes();
    } catch (e: any) {
      this.actionMessage = '✗ Error: ' + (e?.error?.error || e?.message);
    } finally { this.actionLoading = null; }
  }

  verPerfilCompleto() {
    if (!this.selectedCliente) return;
    window.open(`/console/contact-profile/${this.selectedCliente.code}`, '_blank');
  }

  async openCliente(c: ClienteItem) {
    this.drawerOpen = true;
    this.drawerLoading = true;
    this.selectedCliente = null;
    try {
      const r = await this.carteraSvc.getCliente(c.id);
      if (r?.data) this.selectedCliente = r.data;
    } catch (e) { console.error('openCliente:', e); }
    finally { this.drawerLoading = false; }
  }
  closeDrawer() { this.drawerOpen = false; this.selectedCliente = null; }

  async triggerJob(jobKey: string) {
    if (!confirm(`¿Disparar "${jobKey}"?`)) return;
    this.triggering = jobKey;
    try {
      await this.processSvc.trigger(jobKey, {});
      await this.loadActivity();
    } catch (e: any) {
      this.error = `Error: ${e?.error?.error || e?.message}`;
    } finally { this.triggering = null; }
  }
  goToRun(runId: string) { this.router.navigate(['/console/procesos-masivos', runId]); }

  // ── Format helpers ───────────────────────────────────
  fmtMoney(n: number | null | undefined): string {
    if (n == null) return '—';
    return '$' + n.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  fmtInt(n: number | null | undefined): string {
    if (n == null) return '—';
    return n.toLocaleString('es-VE');
  }
  fmtPct(n: number | null | undefined): string {
    if (n == null) return '—';
    return n.toFixed(2) + '%';
  }
  fmtDate(s: string | null | undefined): string {
    if (!s) return '—';
    try { return new Date(s).toLocaleString('es-VE'); } catch { return s; }
  }
  fmtRelative(s: string | null | undefined): string {
    if (!s) return '—';
    const ms = Date.now() - new Date(s).getTime();
    if (ms < 60000) return Math.round(ms / 1000) + 's atrás';
    if (ms < 3600000) return Math.round(ms / 60000) + 'm atrás';
    if (ms < 86400000) return Math.round(ms / 3600000) + 'h atrás';
    return Math.round(ms / 86400000) + 'd atrás';
  }
  moraBadge(d: number): string {
    if (d <= 0) return 'mora-0';
    if (d <= 30) return 'mora-low';
    if (d <= 60) return 'mora-med';
    if (d <= 90) return 'mora-high';
    if (d <= 180) return 'mora-crit';
    return 'mora-extreme';
  }
  statusBadge(status?: string): string {
    switch (status) {
      case 'running': return 'st-running';
      case 'success': return 'st-success';
      case 'partial': return 'st-partial';
      case 'failed':  return 'st-failed';
      case 'cancelled': return 'st-cancelled';
      default: return 'st-default';
    }
  }
  onMoraMinChange(v: any) { this.filters.dias_min = v === '' || v === null ? null : Number(v); }
  onMoraMaxChange(v: any) { this.filters.dias_max = v === '' || v === null ? null : Number(v); }

  bucketPct(count: number): number {
    if (!this.kpis || !this.kpis.en_mora_count) return 0;
    return Math.round((count / this.kpis.en_mora_count) * 100);
  }
}
